import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePlayerStore } from '../../stores/usePlayerStore'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'

interface Brand {
  id: string
  name: string
  description?: string
  reputation: number
  created_at: string
}

export const BrandManagement: React.FC = () => {
  const { t } = useTranslation()
  const { player } = usePlayerStore()
  const [brands, setBrands] = useState<Brand[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newBrand, setNewBrand] = useState({
    name: '',
    description: ''
  })

  useEffect(() => {
    if (player?.id) {
      loadBrands()
    }
  }, [player?.id])

  const loadBrands = async () => {
    setIsLoading(true)
    try {
      // Mock data for now
      const mockBrands: Brand[] = [
        {
          id: 'brand_1',
          name: 'برند طلایی',
          description: 'برند لوکس و با کیفیت',
          reputation: 85,
          created_at: new Date().toISOString()
        }
      ]
      setBrands(mockBrands)
    } catch (error) {
      console.error('Error loading brands:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateBrand = async () => {
    if (!newBrand.name.trim()) {
      alert(t('brands.fillRequiredFields'))
      return
    }

    setIsLoading(true)
    try {
      const brand: Brand = {
        id: `brand_${Date.now()}`,
        name: newBrand.name,
        description: newBrand.description,
        reputation: Math.floor(Math.random() * 40) + 60, // Random reputation between 60-100
        created_at: new Date().toISOString()
      }
      
      setBrands([...brands, brand])
      setNewBrand({ name: '', description: '' })
      setShowCreateForm(false)
      alert(t('brands.brandCreated'))
    } catch (error) {
      console.error('Error creating brand:', error)
      alert(t('brands.brandError'))
    } finally {
      setIsLoading(false)
    }
  }

  const getReputationColor = (reputation: number) => {
    if (reputation >= 80) return 'text-green-600'
    if (reputation >= 60) return 'text-blue-600'
    if (reputation >= 40) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getReputationLabel = (reputation: number) => {
    if (reputation >= 80) return t('brands.excellent')
    if (reputation >= 60) return t('brands.good')
    if (reputation >= 40) return t('brands.average')
    return t('brands.poor')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fa-IR')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card title={`🏷️ ${t('brands.title')}`} subtitle={t('brands.subtitle')}>
        <div className="flex justify-between items-center">
          <div>
            <p className="text-gray-600">
              {t('brands.yourBrands')}: {brands.length} {t('brands.brands')}
            </p>
            <p className="text-sm text-gray-500">
              {t('brands.brandDescription')}
            </p>
          </div>
          <Button
            onClick={() => setShowCreateForm(true)}
            variant="primary"
          >
            {t('brands.createBrand')}
          </Button>
        </div>
      </Card>

      {/* Create Brand Form */}
      {showCreateForm && (
        <Card title={t('brands.createBrand')} subtitle={t('brands.createBrandSubtitle')}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('brands.brandName')} *
              </label>
              <input
                type="text"
                value={newBrand.name}
                onChange={(e) => setNewBrand({ ...newBrand, name: e.target.value })}
                className="input-field"
                placeholder={t('brands.brandNamePlaceholder')}
                maxLength={50}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('brands.brandDescription')}
              </label>
              <textarea
                value={newBrand.description}
                onChange={(e) => setNewBrand({ ...newBrand, description: e.target.value })}
                className="input-field"
                rows={3}
                placeholder={t('brands.brandDescriptionPlaceholder')}
                maxLength={200}
              ></textarea>
            </div>

            <div className="flex justify-end space-x-3 space-x-reverse">
              <Button variant="secondary" onClick={() => setShowCreateForm(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={handleCreateBrand} disabled={isLoading}>
                {isLoading ? t('common.loading') : t('brands.createBrand')}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Brands List */}
      <Card title={t('brands.yourBrands')} subtitle={t('brands.yourBrandsSubtitle')}>
        {isLoading ? (
          <div className="flex justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : brands.length > 0 ? (
          <div className="space-y-4">
            {brands.map((brand) => (
              <div key={brand.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-800">{brand.name}</h3>
                    <p className="text-sm text-gray-600">{brand.description}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${getReputationColor(brand.reputation)}`}>
                      {brand.reputation}%
                    </p>
                    <p className="text-sm text-gray-500">{getReputationLabel(brand.reputation)}</p>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4 space-x-reverse">
                    <div className="text-sm text-gray-500">
                      {t('brands.reputation')}: {brand.reputation}%
                    </div>
                    <div className="text-sm text-gray-500">
                      {t('brands.createdAt')}: {formatDate(brand.created_at)}
                    </div>
                  </div>
                  <div className="flex space-x-2 space-x-reverse">
                    <Button size="sm" variant="secondary">
                      {t('common.edit')}
                    </Button>
                    <Button size="sm" variant="primary">
                      {t('brands.viewProducts')}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🏷️</span>
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              {t('brands.noBrands')}
            </h3>
            <p className="text-gray-500 mb-4">
              {t('brands.createFirstBrand')}
            </p>
            <Button variant="primary" onClick={() => setShowCreateForm(true)}>
              {t('brands.createBrand')}
            </Button>
          </div>
        )}
      </Card>
    </div>
  )
}